import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function SignupPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "trainer" ? "trainer" : "customer";
  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setError("Please accept the Terms of Service and Privacy Policy to continue"); return; }
    setError("");
    setLoading(true);
    const r = await register({ name, email, password, role });
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    toast.success(`Welcome to FitLink, ${r.user.name}!`);
    if (role === "trainer") nav("/trainer");
    else nav("/dashboard");
  };

  return (
    <div className="container-fl py-16 max-w-md">
      <h1 className="text-3xl font-black tracking-tighter mb-2">Create your account</h1>
      <p className="text-neutral-500 text-sm mb-6">Join FitLink Ireland — free, forever.</p>

      <div className="flex gap-2 mb-6">
        <button data-testid="role-customer" onClick={() => setRole("customer")} className={`pill flex-1 justify-center ${role === "customer" ? "pill-active" : ""}`}>I want to train</button>
        <button data-testid="role-trainer" onClick={() => setRole("trainer")} className={`pill flex-1 justify-center ${role === "trainer" ? "pill-active" : ""}`}>I'm a trainer</button>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Full name</label>
          <input data-testid="signup-name" required value={name} onChange={(e) => setName(e.target.value)}
                 className="mt-1 h-12 w-full rounded-md border border-neutral-200 px-3 outline-none focus:border-neutral-900"/>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Email</label>
          <input data-testid="signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="mt-1 h-12 w-full rounded-md border border-neutral-200 px-3 outline-none focus:border-neutral-900"/>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Password (min 6)</label>
          <input data-testid="signup-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                 className="mt-1 h-12 w-full rounded-md border border-neutral-200 px-3 outline-none focus:border-neutral-900"/>
        </div>
        {error && <div data-testid="signup-error" className="text-sm text-red-600">{error}</div>}
        <label className="flex items-start gap-2 text-xs text-neutral-600 cursor-pointer">
          <input data-testid="signup-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                 className="mt-0.5 accent-emerald-600"/>
          <span>
            I agree to FitLink Ireland's <Link to="/legal/terms" className="text-emerald-700 underline" target="_blank">Terms of Service</Link> and <Link to="/legal/privacy" className="text-emerald-700 underline" target="_blank">Privacy Policy</Link>.
          </span>
        </label>
        <button data-testid="signup-submit" type="submit" disabled={loading || !agree} className="btn-primary w-full">
          {loading ? "Creating…" : role === "trainer" ? "Create trainer account" : "Create account"}
        </button>
      </form>
      <div className="text-sm text-neutral-500 mt-6">
        Already have an account? <Link to="/login" className="text-emerald-700 font-semibold">Log in</Link>
      </div>
      {role === "trainer" && (
        <div className="mt-6 border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs rounded-lg p-3">
          After signing up, complete your profile and submit for approval. Our team reviews trainers within 48 hours.
        </div>
      )}
    </div>
  );
}
